import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS, WORKFLOW_ACTION_LABELS, INDICATOR_LABELS, PRIORITY_LABELS, PRIORITIES, CATEGORIES, MATCH_LEVEL_LABELS, STATUS_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Save, GitBranch, Camera, LayoutDashboard, Sparkles, Timer, Repeat, Plus, Trash2, Zap, ShieldCheck } from "lucide-react";

const ALL = "__all__";
const PAUSABLE_STATUSES = ["aberto", "andamento", "aguardando", "aguardando_aprovacao"];

const ROLES = Object.keys(ROLE_LABELS);

function RoleMatrix({ title, icon: Icon, rows, rowLabels, value, onToggle, testPrefix }) {
  return (
    <Card className="p-5 border-border bg-card">
      <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><Icon size={15} className="text-primary" /> {title}</h3>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b border-border">
              <th className="pb-2 pr-4 font-medium" />
              {ROLES.map((r) => <th key={r} className="pb-2 px-2 font-medium text-center">{ROLE_LABELS[r]}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row} className="border-b border-border/40 last:border-0">
                <td className="py-2.5 pr-4 font-medium whitespace-nowrap">{rowLabels[row]}</td>
                {ROLES.map((r) => (
                  <td key={r} className="py-2.5 px-2 text-center">
                    <Checkbox checked={value(row, r)} onCheckedChange={() => onToggle(row, r)} data-testid={`${testPrefix}-${row}-${r}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  /*
   * ================================================================
   * HELPERS DE PARAMETRIZAÇÃO
   * ================================================================
   *
   * Esta página trabalha com um único objeto "settings".
   * Os helpers abaixo atualizam somente o bloco correspondente,
   * preservando todas as demais configurações.
   *
   * A versão anterior do arquivo utilizava setSla, setAp, setRec,
   * setEvidence, setEai, setDc, toggleWorkflow, toggleAI e toggleDash
   * sem declarar essas funções. Isso fazia o React interromper a
   * renderização da página com ReferenceError e deixava a tela branca.
   */

  const patchSettings = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      ...patch,
    }));
  };

  const setWorkflow = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      workflow: {
        ...((prev || {}).workflow || {}),
        ...patch,
      },
    }));
  };

  const setEvidence = (key, value) => {
    setSettings((prev) => ({
      ...(prev || {}),
      evidence: {
        ...((prev || {}).evidence || {}),
        [key]: value,
      },
    }));
  };

  const setSla = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      sla: {
        ...((prev || {}).sla || {}),
        ...patch,
      },
    }));
  };

  const setRec = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      recurrence: {
        ...((prev || {}).recurrence || {}),
        ...patch,
      },
    }));
  };

  const setAp = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      action_plans: {
        ...((prev || {}).action_plans || {}),
        ...patch,
      },
    }));
  };

  const setEai = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      evidence_ai: {
        ...((prev || {}).evidence_ai || {}),
        ...patch,
      },
    }));
  };

  const setDc = (patch) => {
    setSettings((prev) => ({
      ...(prev || {}),
      double_check: {
        ...((prev || {}).double_check || {}),
        ...patch,
      },
    }));
  };

  const toggleWorkflow = (action, role) => {
    setSettings((prev) => {
      const current = (prev || {}).workflow || {};
      const roles = Array.isArray(current[action])
        ? current[action]
        : [];

      return {
        ...(prev || {}),
        workflow: {
          ...current,
          [action]: roles.includes(role)
            ? roles.filter((item) => item !== role)
            : [...roles, role],
        },
      };
    });
  };

  const toggleAI = (role) => {
    setSettings((prev) => {
      const current = Array.isArray((prev || {}).ai_roles)
        ? (prev || {}).ai_roles
        : [];

      return {
        ...(prev || {}),
        ai_roles: current.includes(role)
          ? current.filter((item) => item !== role)
          : [...current, role],
      };
    });
  };

  const toggleDash = (indicator, role) => {
    setSettings((prev) => {
      const current = (prev || {}).dashboard_visibility || {};
      const roleSettings = {
        ...(current[role] || {}),
      };

      roleSettings[indicator] = !roleSettings[indicator];

      return {
        ...(prev || {}),
        dashboard_visibility: {
          ...current,
          [role]: roleSettings,
        },
      };
    });
  };

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        setSettings(data || {});
      })
      .catch((err) => {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail
          )
        );
        setSettings({});
      });

    api
      .get("/clients")
      .then(({ data }) => setClients(data || []))
      .catch(() => setClients([]));
  }, []);

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  /*
   * Parametrizações são configurações por perfil/sistema.
   * A delegação individual de usuários NÃO é gravada aqui.
   * Ela continua sendo feita pela Matriz de Permissões.
   */
  const workflow = settings.workflow || {};
  const evidence = settings.evidence || {};
  const aiRoles = settings.ai_roles || [];
  const dashboardVisibility = settings.dashboard_visibility || {};
  const sla = settings.sla || {};
  const recurrence = settings.recurrence || {};
  const actionPlans = settings.action_plans || {};
  const evidenceAi = settings.evidence_ai || {};
  const doubleCheck = settings.double_check || {};

  const updateRule = (i, patch) => setSla({ rules: settings.sla.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const updatePlanRule = (i, patch) => setAp({ rules: settings.action_plans.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Parametrizações salvas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings size={26} className="text-primary" /> Administração do Sistema
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
          Fluxos e parametrizações padrão por perfil. Delegações individuais ficam na Matriz de Permissões.
          A Central de Notificações foi centralizada na Administração da Plataforma.
        </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="settings-save-btn">
          <Save size={15} className="mr-2" /> {saving ? "Salvando..." : "Salvar Tudo"}
        </Button>
      </div>

      <RoleMatrix
        title="Fluxos de Ocorrência — quem pode fazer o quê"
        icon={GitBranch}
        rows={Object.keys(WORKFLOW_ACTION_LABELS)}
        rowLabels={WORKFLOW_ACTION_LABELS}
        value={(action, role) => (settings.workflow?.[action] || []).includes(role)}
        onToggle={toggleWorkflow}
        testPrefix="wf"
      />

      <Card className="p-5 border-border bg-card">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><Camera size={15} className="text-primary" /> Evidências obrigatórias para conclusão</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
            <span className="text-sm">Mínimo de fotos</span>
            <Input type="number" min={0} max={20} value={settings.evidence?.min_photos ?? 0}
              onChange={(e) => setEvidence("min_photos", Math.max(0, parseInt(e.target.value || "0")))}
              className="w-20 h-8 text-center" data-testid="evidence-min-photos-input" />
          </div>
          {[["require_comment", "Obrigar descrição da ação"], ["require_gps", "Obrigar localização GPS"], ["require_signature", "Obrigar assinatura"]].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
              <span className="text-sm">{label}</span>
              <Switch checked={!!settings.evidence?.[k]} onCheckedChange={(v) => setEvidence(k, v)} data-testid={`evidence-${k}-switch`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-border bg-card">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><Sparkles size={15} className="text-primary" /> ASTRAL AI — perfis autorizados</h3>
        <div className="flex flex-wrap gap-2.5">
          {ROLES.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm rounded-md border border-border px-3.5 py-2 cursor-pointer hover:border-primary/50 transition-colors">
              <Checkbox checked={(settings.ai_roles || []).includes(r)} onCheckedChange={() => toggleAI(r)} data-testid={`ai-role-${r}`} />
              {ROLE_LABELS[r]}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">O administrador sempre tem acesso. Permissões individuais na Matriz de Permissões sobrescrevem o padrão do perfil.</p>
      </Card>

      <Card className="p-5 border-border bg-card" data-testid="sla-settings-card">
        <h3 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2"><Timer size={15} className="text-primary" /> SLA Inteligente — prazos, semáforo e escalonamento</h3>
        <p className="text-xs text-muted-foreground mb-4">Regras específicas por cliente/categoria têm prioridade sobre as regras gerais.</p>
        <div className="space-y-2">
          {(settings.sla.rules || []).map((r, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center rounded-md border border-border p-2" data-testid={`sla-rule-row-${i}`}>
              <Select value={r.priority} onValueChange={(v) => updateRule(i, { priority: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`sla-rule-priority-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={r.category || ALL} onValueChange={(v) => updateRule(i, { category: v === ALL ? null : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`sla-rule-category-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas categorias</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.client_id || ALL} onValueChange={(v) => updateRule(i, { client_id: v === ALL ? null : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`sla-rule-client-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos clientes</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} value={r.hours} className="h-8 text-center"
                  onChange={(e) => updateRule(i, { hours: Math.max(1, parseInt(e.target.value || "1")) })} data-testid={`sla-rule-hours-${i}`} />
                <span className="text-xs text-muted-foreground">horas</span>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setSla({ rules: settings.sla.rules.filter((_, j) => j !== i) })} data-testid={`sla-rule-remove-${i}`}>
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="text-xs" data-testid="sla-rule-add-btn"
            onClick={() => setSla({ rules: [...(settings.sla.rules || []), { priority: "media", category: null, client_id: null, hours: 72 }] })}>
            <Plus size={13} className="mr-1" /> Adicionar regra de SLA
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Semáforo amarelo a partir de (%)</p>
            <Input type="number" min={1} max={99} value={settings.sla.thresholds?.yellow ?? 70} className="h-8 text-center"
              onChange={(e) => setSla({ thresholds: { ...settings.sla.thresholds, yellow: parseInt(e.target.value || "70") } })} data-testid="sla-threshold-yellow-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Semáforo laranja a partir de (%)</p>
            <Input type="number" min={1} max={99} value={settings.sla.thresholds?.orange ?? 90} className="h-8 text-center"
              onChange={(e) => setSla({ thresholds: { ...settings.sla.thresholds, orange: parseInt(e.target.value || "90") } })} data-testid="sla-threshold-orange-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Escalonar ao superior após (h do estouro)</p>
            <Input type="number" min={0} value={settings.sla.escalation?.level2_hours ?? 2} className="h-8 text-center"
              onChange={(e) => setSla({ escalation: { ...settings.sla.escalation, level2_hours: parseFloat(e.target.value || "2") } })} data-testid="sla-escalation-level2-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Escalonar à administração após (h)</p>
            <Input type="number" min={0} value={settings.sla.escalation?.level3_hours ?? 24} className="h-8 text-center"
              onChange={(e) => setSla({ escalation: { ...settings.sla.escalation, level3_hours: parseFloat(e.target.value || "24") } })} data-testid="sla-escalation-level3-input" />
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-2">Status que pausam a contagem do SLA:</p>
          <div className="flex flex-wrap gap-2.5">
            {PAUSABLE_STATUSES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm rounded-md border border-border px-3.5 py-2 cursor-pointer hover:border-primary/50 transition-colors">
                <Checkbox checked={(settings.sla.pause_statuses || []).includes(s)} data-testid={`sla-pause-${s}`}
                  onCheckedChange={() => {
                    const list = settings.sla.pause_statuses || [];
                    setSla({ pause_statuses: list.includes(s) ? list.filter((x) => x !== s) : [...list, s] });
                  }} />
                {STATUS_LABELS[s]}
              </label>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border bg-card" data-testid="recurrence-settings-card">
        <h3 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2"><Repeat size={15} className="text-primary" /> Reincidências Inteligentes</h3>
        <p className="text-xs text-muted-foreground mb-4">Detecção automática de ocorrências repetidas da mesma categoria no mesmo local.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Janela de análise (dias)</p>
            <Input type="number" min={1} value={settings.recurrence?.window_days ?? 30} className="h-8 text-center"
              onChange={(e) => setRec({ window_days: Math.max(1, parseInt(e.target.value || "30")) })} data-testid="recurrence-window-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Nível de comparação</p>
            <Select value={settings.recurrence?.match_level || "categoria"} onValueChange={(v) => setRec({ match_level: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="recurrence-match-level-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MATCH_LEVEL_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {[["reincidencia", "Reincidência a partir de"], ["recorrente", "Recorrente a partir de"], ["critica", "Crítica a partir de"]].map(([k, label]) => (
            <div key={k} className="rounded-md border border-border px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1.5">{label} (nº)</p>
              <Input type="number" min={2} value={settings.recurrence.thresholds?.[k] ?? 2} className="h-8 text-center"
                onChange={(e) => setRec({ thresholds: { ...settings.recurrence.thresholds, [k]: Math.max(2, parseInt(e.target.value || "2")) } })}
                data-testid={`recurrence-threshold-${k}-input`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-border bg-card" data-testid="plans-settings-card">
        <h3 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2"><Zap size={15} className="text-primary" /> Automação de Planos de Ação</h3>
        <p className="text-xs text-muted-foreground mb-4">Geração automática de planos de ação por reincidência e eventos críticos.</p>
        <div className="flex flex-wrap gap-2.5 mb-4">
          {[["recurrence", "Reincidência"], ["critical_occurrence", "Ocorrência crítica"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm rounded-md border border-border px-3.5 py-2 cursor-pointer hover:border-primary/50 transition-colors">
              <Checkbox checked={!!settings.action_plans.auto_triggers?.[k]} data-testid={`plan-trigger-${k}`}
                onCheckedChange={(v) => setAp({ auto_triggers: { ...settings.action_plans.auto_triggers, [k]: !!v } })} />
              Gerar por {label}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Reincidências para gerar plano (padrão)</p>
            <Input type="number" min={2} value={settings.action_plans?.recurrence_threshold ?? 3} className="h-8 text-center"
              onChange={(e) => setAp({ recurrence_threshold: Math.max(2, parseInt(e.target.value || "3")) })} data-testid="plan-threshold-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Avaliar efetividade após (dias)</p>
            <Input type="number" min={7} value={settings.action_plans?.effectiveness_days ?? 90} className="h-8 text-center"
              onChange={(e) => setAp({ effectiveness_days: Math.max(7, parseInt(e.target.value || "90")) })} data-testid="plan-effectiveness-days-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Máx. recorrências p/ "Parcialmente efetivo"</p>
            <Input type="number" min={1} value={settings.action_plans?.effectiveness_partial_max ?? 2} className="h-8 text-center"
              onChange={(e) => setAp({ effectiveness_partial_max: Math.max(1, parseInt(e.target.value || "2")) })} data-testid="plan-partial-max-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3 space-y-2.5">
            <label className="flex items-center justify-between gap-2 text-xs">
              Narrativa IA automática
              <Switch checked={!!settings.action_plans?.ai_auto_generate} data-testid="plan-ai-auto-switch"
                onCheckedChange={(v) => setAp({ ai_auto_generate: v })} />
            </label>
            <label className="flex items-center justify-between gap-2 text-xs">
              Notificar cliente
              <Switch checked={!!settings.action_plans?.notify_client} data-testid="plan-notify-client-switch"
                onCheckedChange={(v) => setAp({ notify_client: v })} />
            </label>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-2">Prazo padrão por prioridade (dias):</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["baixa", "media", "alta", "critica"].map((p) => (
              <div key={p} className="rounded-md border border-border px-4 py-3">
                <p className="text-xs text-muted-foreground mb-1.5">{PRIORITY_LABELS[p]}</p>
                <Input type="number" min={0.5} step={0.5} value={settings.action_plans.priority_deadlines_days?.[p] ?? 7} className="h-8 text-center"
                  onChange={(e) => setAp({ priority_deadlines_days: { ...settings.action_plans.priority_deadlines_days, [p]: parseFloat(e.target.value || "7") } })}
                  data-testid={`plan-deadline-${p}-input`} />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <p className="text-xs text-muted-foreground">Regras específicas de reincidência (cliente/categoria — mais específica vence):</p>
          {(settings.action_plans.rules || []).map((r, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center rounded-md border border-border p-2" data-testid={`plan-rule-row-${i}`}>
              <Select value={r.client_id || ALL} onValueChange={(v) => updatePlanRule(i, { client_id: v === ALL ? null : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`plan-rule-client-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos clientes</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.category || ALL} onValueChange={(v) => updatePlanRule(i, { category: v === ALL ? null : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid={`plan-rule-category-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todas categorias</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="number" min={2} value={r.threshold} className="h-8 text-center"
                  onChange={(e) => updatePlanRule(i, { threshold: Math.max(2, parseInt(e.target.value || "3")) })} data-testid={`plan-rule-threshold-${i}`} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">ocorrências</span>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setAp({ rules: settings.action_plans.rules.filter((_, j) => j !== i) })} data-testid={`plan-rule-remove-${i}`}>
                  <Trash2 size={13} />
                </Button>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" data-testid="plan-rule-add-btn"
              onClick={() => setAp({ rules: [...(settings.action_plans.rules || []), { client_id: null, category: null, threshold: 3 }] })}>
              <Plus size={13} className="mr-1" /> Adicionar regra
            </Button>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" data-testid="plan-restore-defaults-btn"
              onClick={() => setAp({
                auto_triggers: { recurrence: true, critical_occurrence: false, sla_overdue: false },
                recurrence_threshold: 3, rules: [], default_priority: "alta",
                priority_deadlines_days: { baixa: 30, media: 15, alta: 7, critica: 1 },
                notify_client: false, ai_auto_generate: true, effectiveness_days: 90, effectiveness_partial_max: 2,
              })}>
              Restaurar configuração padrão
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border bg-card" data-testid="evidence-ai-settings-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Sparkles size={15} className="text-primary" /> Política de Validação Inteligente de Evidências</h3>
          <label className="flex items-center gap-2 text-xs">
            Ativada
            <Switch checked={!!settings.evidence_ai.enabled} data-testid="evidence-ai-enabled-switch"
              onCheckedChange={(v) => setEai({ enabled: v })} />
          </label>
        </div>
        <p className="text-xs text-muted-foreground mb-4">A IA analisa qualidade, relevância e correção nas fotos ao solicitar conclusão. Se indisponível, o fluxo segue para validação manual do supervisor — nunca bloqueia a operação.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
          {[
            ["informar", "🟢 Apenas Informar", "Gera parecer técnico sem interferir no fluxo"],
            ["alertar", "🟡 Alertar (recomendado)", "Exibe alertas; supervisor aprova com justificativa"],
            ["solicitar_nova", "🟠 Solicitar Nova Evidência", "Abaixo da pontuação mínima, pede novas fotos"],
            ["bloquear", "🔴 Bloquear", "Impede a conclusão até novas evidências"],
          ].map(([k, label, desc]) => (
            <button key={k} onClick={() => setEai({ mode: k })} data-testid={`evidence-ai-mode-${k}`}
              className={`text-left rounded-md border px-3.5 py-2.5 transition-colors ${settings.evidence_ai.mode === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
              <p className="text-sm font-medium">{label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Pontuação mínima (0-100)</p>
            <Input type="number" min={0} max={100} value={settings.evidence_ai?.min_score ?? 60} className="h-8 text-center"
              onChange={(e) => setEai({ min_score: Math.min(100, Math.max(0, parseInt(e.target.value || "60"))) })} data-testid="evidence-ai-minscore-input" />
          </div>
          <div className="rounded-md border border-border px-4 py-3 sm:col-span-3">
            <p className="text-xs text-muted-foreground mb-1.5">Categorias dispensadas da validação IA:</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const exempt = (settings.evidence_ai.categories_exempt || []).includes(c);
                return (
                  <button key={c} onClick={() => setEai({
                    categories_exempt: exempt
                      ? settings.evidence_ai.categories_exempt.filter((x) => x !== c)
                      : [...(settings.evidence_ai.categories_exempt || []), c],
                  })} data-testid={`evidence-ai-exempt-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${exempt ? "border-amber-500/50 bg-amber-500/15 text-amber-400" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border bg-card" data-testid="double-check-settings-card">
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-1">
          <ShieldCheck size={15} className="text-cyan-400" /> Double Check em Campo — validação presencial
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Ao aprovar a conclusão, o aprovador precisa registrar foto no local e GPS capturado na hora, com verificação de proximidade quando a ocorrência tiver coordenadas.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
          {[
            ["nunca", "Nunca", "Aprovação sem validação presencial"],
            ["criticas", "Apenas Críticas (recomendado)", "Exige presença nas ocorrências críticas"],
            ["criticas_altas", "Críticas e Altas", "Exige presença em críticas e altas"],
            ["sempre", "Sempre", "Toda aprovação exige presença no local"],
          ].map(([k, label, desc]) => (
            <button key={k} onClick={() => setDc({ mode: k })} data-testid={`double-check-mode-${k}`}
              className={`text-left rounded-md border px-3.5 py-2.5 transition-colors ${(settings.double_check?.mode || "criticas") === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
              <p className="text-sm font-medium">{label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-md border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-1.5">Tolerância GPS (metros)</p>
            <Input type="number" min={10} max={5000} value={settings.double_check?.gps_tolerance_m ?? 100} className="h-8 text-center"
              onChange={(e) => setDc({ gps_tolerance_m: Math.max(10, parseInt(e.target.value || "100")) })} data-testid="double-check-tolerance-input" />
          </div>
          <label className="rounded-md border border-border px-4 py-3 flex items-center justify-between gap-2 text-sm cursor-pointer">
            Foto no local obrigatória
            <Switch checked={settings.double_check?.require_photo !== false}
              onCheckedChange={(v) => setDc({ require_photo: v })} data-testid="double-check-photo-switch" />
          </label>
          <label className="rounded-md border border-border px-4 py-3 flex items-center justify-between gap-2 text-sm cursor-pointer">
            Bloquear fora do raio de tolerância
            <Switch checked={settings.double_check?.block_outside_radius !== false}
              onCheckedChange={(v) => setDc({ block_outside_radius: v })} data-testid="double-check-block-switch" />
          </label>
        </div>
      </Card>

      <RoleMatrix
        title="Dashboard — indicadores visíveis por perfil"
        icon={LayoutDashboard}
        rows={Object.keys(INDICATOR_LABELS)}
        rowLabels={INDICATOR_LABELS}
        value={(ind, role) => !!(settings.dashboard_visibility?.[role] || {})[ind]}
        onToggle={toggleDash}
        testPrefix="dash"
      />
    </div>
  );
}